import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import { Play, ArrowRight, ArrowUpRight } from "lucide-react";
import api from "@/lib/api";
import { FadeUp, Eyebrow } from "@/components/Motion";
import SEO from "@/components/SEO";
import ProjectCard from "@/components/ProjectCard";
import TrailerModal from "@/components/TrailerModal";
import { formatDate } from "@/lib/api";

const FILTERS = [
  { k: "all", label: "All Work" },
  { k: "original", label: "Original Work" },
  { k: "service", label: "Service Production" },
];

const CHAPTERS = [
  { n: "01", title: "Cinematic Autonomy", body: "We develop and own our original slate — features, docs, and shorts built from the ground up, answerable only to the story.", accent: "text-orange" },
  { n: "02", title: "Toronto Roots, Global Scope", body: "Our crews, our stages, our city. We bring international productions to Toronto and take Toronto stories to the world's festivals.", accent: "text-blue" },
  { n: "03", title: "Hyper-Collaborative Craft", body: "Local crews — union and non-union — plus partnered vendors for gear, stages, and post. We build the right team for the budget, so every dollar ends up on screen.", accent: "text-violet" },
];

const SPANS = ["lg:col-span-7", "lg:col-span-5", "lg:col-span-4", "lg:col-span-4", "lg:col-span-4", "lg:col-span-6", "lg:col-span-6"];

function IntroOverlay({ onDone }) {
  useEffect(() => {
    const t = setTimeout(onDone, 1500);
    return () => clearTimeout(t);
  }, [onDone]);
  return (
    <motion.div
      className="fixed inset-0 z-[90] flex flex-col items-center justify-center bg-ink"
      style={{ willChange: "transform" }}
      exit={{ y: "-100%", transition: { duration: 0.8, ease: [0.83, 0, 0.17, 1] } }}
      data-testid="intro-overlay"
    >
      <motion.img
        src="/logo-full.webp"
        alt="Incolour Films"
        data-testid="intro-full-logo"
        className="w-[min(78vw,540px)] px-6"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      />
      <motion.p
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.55, duration: 0.7 }}
        className="mt-7 font-serif text-2xl italic text-orange sm:text-3xl"
        data-testid="intro-slogan"
      >
        Think Incolour.
      </motion.p>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.45 }}
        className="absolute bottom-10 text-xs font-mono uppercase tracking-[0.5em] text-cream/50"
      >
        Toronto — Est. 2019
      </motion.p>
    </motion.div>
  );
}

export default function Home() {
  const [projects, setProjects] = useState([]);
  const [journal, setJournal] = useState([]);
  const [filter, setFilter] = useState("all");
  const [trailer, setTrailer] = useState(null);
  const [introDone, setIntroDone] = useState(false);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.02, 1.16]);

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
    api.get("/journal").then((r) => setJournal(r.data.slice(0, 3))).catch(() => {});
  }, []);

  const featured = projects.find((p) => p.featured) || projects[0];
  const gridProjects = projects.filter((p) => !p.in_development);
  const filtered = filter === "all" ? gridProjects : gridProjects.filter((p) => p.category === filter);
  const countFor = (k) => (k === "all" ? gridProjects.length : gridProjects.filter((p) => p.category === k).length);

  return (
    <div data-testid="home-page">
      <SEO
        title="Film Production & Service Production in Toronto"
        description="Incolour Films is a Toronto film production company creating original films and providing service production for film, television, commercials and branded content."
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Incolour Films",
          alternateName: "Incolour Media",
          url: "https://incolourfilms.ca",
          logo: "https://incolourfilms.ca/logo-mark.webp",
          email: "connect@incolourfilms.ca",
          sameAs: [
            "https://instagram.com/incolour.media",
            "https://vimeo.com/incolourmedia",
            "https://linkedin.com/company/incolour-media",
          ],
          address: { "@type": "PostalAddress", addressLocality: "Toronto", addressRegion: "ON", addressCountry: "CA" },
        }}
      />
      <AnimatePresence>{!introDone && <IntroOverlay onDone={() => setIntroDone(true)} />}</AnimatePresence>

      {/* HERO */}
      <section ref={heroRef} className="relative flex min-h-[94svh] items-end overflow-hidden bg-ink text-cream">
        {featured && (
          <motion.img
            src={featured.poster}
            alt={featured.title}
            style={{ y: bgY, scale: bgScale }}
            className="absolute inset-0 h-full w-full object-cover opacity-75"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-ink/95 via-ink/30 to-transparent" />

        <div className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-24 pt-32 sm:px-6 lg:px-8">
          {featured && (
            <>
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: introDone ? 0.1 : 1.15, duration: 0.6 }}
                className="mt-6 flex flex-wrap items-center gap-2"
              >
                <span className="rounded-full bg-orange px-3.5 py-1.5 text-[10px] font-mono uppercase tracking-[0.18em] text-cream">Featured</span>
                <span className="rounded-full bg-cream/15 px-3.5 py-1.5 text-[10px] font-mono uppercase tracking-[0.18em] text-cream backdrop-blur">
                  {featured.type} · {featured.year}
                </span>
              </motion.div>
              <h1 className="mt-5 font-display text-5xl font-extrabold leading-[1.0] tracking-tight sm:text-7xl lg:text-8xl" data-testid="hero-title">
                <span className="block overflow-hidden pb-[0.14em] -mb-[0.14em]">
                  <motion.span
                    className="block"
                    initial={{ y: "115%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 1, delay: introDone ? 0.05 : 1.3, ease: [0.16, 1, 0.3, 1] }}
                  >
                    {featured.title}
                  </motion.span>
                </span>
              </h1>
              <FadeUp delay={introDone ? 0.35 : 1.6} className="mt-5">
                {featured.director && (
                  <p className="flex flex-wrap items-center gap-x-2.5 text-sm text-cream/80">
                    Written and Directed by{" "}
                    <a
                      href="https://www.imdb.com/name/nm9719155/"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="hero-director-link"
                      className="font-semibold text-cream underline decoration-orange/60 underline-offset-4 transition-colors hover:text-orange"
                    >
                      {featured.director}
                    </a>
                    {featured.credits?.find((c) => /starring|cast/i.test(c.role)) && (
                      <>
                        <span className="text-orange">·</span>
                        <span data-testid="hero-starring">
                          Starring <span className="font-semibold text-cream">{featured.credits.find((c) => /starring|cast/i.test(c.role)).name}</span>
                        </span>
                      </>
                    )}
                  </p>
                )}
              <div className="mt-8 flex flex-wrap items-center gap-4">
                {featured.trailer_url && (
                  <button
                    onClick={() => setTrailer(featured)}
                    data-testid="hero-play-trailer-btn"
                    className="group flex items-center gap-3 rounded-full bg-orange px-6 py-3.5 text-sm font-semibold text-cream transition-transform hover:scale-[1.04]"
                  >
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-cream/20">
                      <Play size={12} className="fill-cream" />
                    </span>
                    Watch trailer
                  </button>
                )}
                <Link
                  to={`/projects/${featured.slug}`}
                  data-testid="hero-view-project-link"
                  className="flex items-center gap-2 rounded-full border border-cream/40 px-6 py-3.5 text-sm font-semibold text-cream transition-colors hover:border-cream hover:bg-cream hover:text-ink"
                >
                  {featured.title} <ArrowUpRight size={16} />
                </Link>
              </div>
            </FadeUp>
            </>
          )}
        </div>
      </section>

      {/* WORK GRID */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28" data-testid="work-section">
        <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
          <div>
            <Eyebrow>Selected Work</Eyebrow>
            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">The work, first.</h2>
          </div>
          <div className="flex flex-wrap gap-2" role="tablist" aria-label="Filter work">
            {FILTERS.map((f) => (
              <button
                key={f.k}
                role="tab"
                aria-selected={filter === f.k}
                onClick={() => setFilter(f.k)}
                data-testid={`work-filter-${f.k}`}
                className={`rounded-full border px-4 py-2 text-xs font-mono uppercase tracking-[0.15em] transition-colors ${
                  filter === f.k
                    ? "border-ink bg-ink text-cream dark:border-cream dark:bg-cream dark:text-ink"
                    : "border-border text-muted-foreground hover:border-ink hover:text-foreground dark:hover:border-cream"
                }`}
              >
                {f.label} <span className="text-orange">{countFor(f.k)}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-12 lg:gap-8">
          {filtered.map((p, i) => (
            <ProjectCard key={p.id} p={p} i={i} className={`md:col-span-1 ${SPANS[i % SPANS.length]}`} />
          ))}
        </div>
        {filtered.length === 0 && <p className="mt-10 text-muted-foreground">Loading work…</p>}
      </section>

      {/* CTA */}
      <section className="bg-ink py-12 text-cream lg:py-14" data-testid="cta-section">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-6 px-4 sm:px-6 lg:flex-row lg:items-center lg:px-8">
          <FadeUp>
            <h2 className="font-display text-2xl font-extrabold tracking-tight sm:text-3xl lg:whitespace-nowrap lg:text-4xl">
              Are you ready to <span className="font-serif italic text-orange">Think Incolour</span>?
            </h2>
          </FadeUp>
          <FadeUp delay={0.1}>
            <Link
              to="/contact"
              data-testid="cta-contact-btn"
              className="inline-flex items-center gap-3 rounded-full bg-cream px-7 py-3.5 text-sm font-semibold text-ink transition-transform hover:scale-[1.04]"
            >
              Start a project <ArrowUpRight size={16} />
            </Link>
          </FadeUp>
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="border-y border-border bg-cream2 py-20 dark:bg-card lg:py-28" data-testid="manifesto-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Eyebrow>How we work</Eyebrow>
          <h2 className="mt-4 max-w-2xl font-display text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">
            Three chapters, one studio.
          </h2>
          <div className="mt-12 grid gap-6 lg:grid-cols-3">
            {CHAPTERS.map((c, i) => (
              <FadeUp key={c.n} delay={i * 0.12}>
                <div className="group h-full rounded-2xl border border-border bg-background p-8 transition-all duration-300 hover:-translate-y-1 hover:border-orange">
                  <p className={`font-mono text-5xl font-medium ${c.accent}`}>{c.n}</p>
                  <h3 className="mt-6 font-display text-xl font-bold tracking-tight sm:text-2xl">{c.title}</h3>
                  <p className="mt-4 text-sm leading-relaxed text-muted-foreground sm:text-base">{c.body}</p>
                </div>
              </FadeUp>
            ))}
          </div>
          <FadeUp delay={0.2} className="mt-10">
            <Link to="/about" data-testid="manifesto-about-link" className="group inline-flex items-center gap-2 text-sm font-semibold text-foreground dark:text-cream">
              Read the full story
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </FadeUp>
        </div>
      </section>

      {/* JOURNAL TEASER */}
      {journal.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28" data-testid="journal-teaser">
          <div className="flex items-end justify-between gap-6">
            <div>
              <Eyebrow>Journal</Eyebrow>
              <h2 className="mt-4 font-display text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">Notes from the studio</h2>
            </div>
            <Link to="/journal" className="group hidden items-center gap-2 text-sm font-semibold sm:inline-flex" data-testid="journal-view-all-link">
              All entries <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {journal.map((j, i) => (
              <FadeUp key={j.id} delay={i * 0.1}>
                <Link to={`/journal/${j.slug}`} className="group block" data-testid={`journal-teaser-${j.slug}`}>
                  <div className="overflow-hidden rounded-2xl border border-border">
                    <div className="aspect-[16/10] overflow-hidden bg-cream2 dark:bg-card">
                      <img src={j.image} alt={j.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.05]" />
                    </div>
                  </div>
                  <p className="mt-4 text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">
                    <span className="text-orange">{j.category}</span> — {formatDate(j.published_at)}
                  </p>
                  <h3 className="mt-2 font-display text-xl font-bold tracking-tight transition-colors group-hover:text-orange">{j.title}</h3>
                </Link>
              </FadeUp>
            ))}
          </div>
        </section>
      )}

      <TrailerModal url={trailer?.trailer_url} title={trailer?.title} open={!!trailer} onClose={() => setTrailer(null)} />
    </div>
  );
}
